import React, { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../utils/apiClient.js";
import useAutoRefresh from "../../hooks/useAutoRefresh.js";
import Spinner from "../../components/Spinner.js";
import "./Admin.css";

// Status colors reuse the admin panel's existing brand roles (teal/amber/red/grey) rather than
// introducing new hues — these already carry "good/needs attention/negative/neutral" meaning
// elsewhere in this UI (approve buttons, warning notices, delete actions).
const ROLE_COLOR = { good: "#0f8d7d", warning: "#b8860b", critical: "#c50a0a", neutral: "#8095a1" };

const STATUS_ROLE = {
  new: "warning",
  contacted: "good",
  follow_up_required: "warning",
  converted: "good",
  closed: "neutral",
  pending: "warning",
  approved: "good",
  in_progress: "good",
  completed: "good",
  cancelled: "critical",
  read: "neutral",
  replied: "good",
};

const formatLabel = (key) => key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;
const formatDayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

// A slim segmented bar showing how `total` splits across statuses, colored by status role (never
// by arbitrary category order — these are state indicators, not identity), with every non-zero
// segment direct-labeled below so color never has to carry meaning alone.
const StatusBreakdown = ({ byStatus, total }) => {
  const entries = Object.entries(byStatus).filter(([, count]) => count > 0);
  if (total === 0) {
    return <p className="admin-subtle">No records yet.</p>;
  }
  return (
    <div className="admin-status-breakdown">
      <div className="admin-status-bar" role="img" aria-label="Status breakdown">
        {entries.map(([status, count]) => (
          <div
            key={status}
            className="admin-status-bar-segment"
            style={{ width: `${(count / total) * 100}%`, background: ROLE_COLOR[STATUS_ROLE[status]] || ROLE_COLOR.neutral }}
            title={`${formatLabel(status)}: ${count}`}
          />
        ))}
      </div>
      <div className="admin-status-legend">
        {entries.map(([status, count]) => (
          <span className="admin-status-legend-item" key={status}>
            <span
              className="admin-status-legend-dot"
              style={{ background: ROLE_COLOR[STATUS_ROLE[status]] || ROLE_COLOR.neutral }}
            />
            {formatLabel(status)} <strong>{count}</strong>
          </span>
        ))}
      </div>
    </div>
  );
};

// Minimal inline sparkline — no charting library, since this is a lightweight trend visual, not a
// full BI dashboard. Single-hue line, rounded ends, filled area, with a hover crosshair + tooltip
// (the interactive layer a line chart gets by default).
const Sparkline = ({ data, color, valueFormatter = (v) => v }) => {
  const svgRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const width = 280;
  const height = 64;
  const paddingX = 4;
  const paddingY = 8;

  const values = data.map((d) => d.value);
  const maxValue = Math.max(...values, 1);
  const stepX = (width - paddingX * 2) / Math.max(data.length - 1, 1);

  const pointFor = (i) => {
    const x = paddingX + i * stepX;
    const y = height - paddingY - (values[i] / maxValue) * (height - paddingY * 2);
    return [x, y];
  };

  const linePath = data.map((_, i) => pointFor(i)).map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${(paddingX + (data.length - 1) * stepX).toFixed(1)},${height - paddingY} L${paddingX},${height - paddingY} Z`;

  const handleMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const relativeX = ((e.clientX - rect.left) / rect.width) * width;
    const index = Math.round((relativeX - paddingX) / stepX);
    setHoverIndex(Math.max(0, Math.min(data.length - 1, index)));
  };

  const hovered = hoverIndex !== null ? data[hoverIndex] : null;
  const hoveredPoint = hoverIndex !== null ? pointFor(hoverIndex) : null;

  return (
    <div className="admin-sparkline-wrap">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="admin-sparkline"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <path d={areaPath} fill={color} opacity="0.12" stroke="none" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {hoveredPoint && (
          <>
            <line x1={hoveredPoint[0]} x2={hoveredPoint[0]} y1={paddingY} y2={height - paddingY} stroke="#c3c2b7" strokeWidth="1" />
            <circle cx={hoveredPoint[0]} cy={hoveredPoint[1]} r="3.5" fill={color} stroke="#fff" strokeWidth="1.5" />
          </>
        )}
      </svg>
      {hovered && (
        <div className="admin-sparkline-tooltip">
          <strong>{valueFormatter(hovered.value)}</strong> · {formatDayLabel(hovered.date)}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, total, children }) => (
  <div className="admin-stat-card">
    <div className="admin-stat-card-header">
      <span className="admin-stat-icon" aria-hidden="true">{icon}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
    <div className="admin-stat-number">{total}</div>
    {children}
  </div>
);

const DashboardPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const { data } = await apiClient.get("/analytics/summary");
      setSummary(data);
    } catch (err) {
      if (!silent) setError("Could not load dashboard statistics.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useAutoRefresh(useCallback(() => load(true), [load]));

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Dashboard</h1>
        <p>Live overview across quote leads, bookings, contacts and revenue</p>
      </div>

      {error && <p className="admin-error">{error}</p>}

      {loading ? (
        <Spinner label="Loading dashboard…" />
      ) : !summary ? null : (
        <div className="admin-stats-grid">
          <StatCard icon="📝" label="Quote Leads" total={summary.quoteLeads.total}>
            <StatusBreakdown byStatus={summary.quoteLeads.byStatus} total={summary.quoteLeads.total} />
          </StatCard>

          <StatCard icon="📅" label="Bookings" total={summary.bookings.total}>
            <StatusBreakdown byStatus={summary.bookings.byStatus} total={summary.bookings.total} />
            <p className="admin-sparkline-caption">New bookings, last 14 days</p>
            <Sparkline data={summary.trends.bookings} color={ROLE_COLOR.good} />
          </StatCard>

          <StatCard icon="✉️" label="Contact Messages" total={summary.contacts.total}>
            <StatusBreakdown byStatus={summary.contacts.byStatus} total={summary.contacts.total} />
          </StatCard>

          <StatCard icon="💰" label="Revenue this month" total={formatCurrency(summary.revenue.thisMonth)}>
            <p className="admin-sparkline-caption">Invoiced revenue, last 14 days</p>
            <Sparkline data={summary.trends.revenue} color={ROLE_COLOR.good} valueFormatter={formatCurrency} />
            <ul className="admin-stat-breakdown admin-stat-breakdown-plain">
              <li>
                <span>All time</span>
                <strong>{formatCurrency(summary.revenue.allTime)}</strong>
              </li>
              <li>
                <span>Invoices issued</span>
                <strong>{summary.revenue.invoiceCount}</strong>
              </li>
            </ul>
          </StatCard>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
